import classNames from 'classnames';
import {
  forwardRef,
  useCallback,
  type ComponentType,
  type EventHandler,
  type ForwardRefRenderFunction,
  type FunctionComponent,
  type JSX,
  type PropsWithChildren,
  type PropsWithoutRef,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type RefAttributes,
} from 'react';

import Tooltip from '@atlaskit/tooltip';
import { useFocusRing, type FocusRingEventListeners } from '@trello/a11y';
import { RouterLink } from '@trello/router/router-link';
import type { TestId } from '@trello/test-ids';

import * as styles from './PopoverMenu.module.less';

type ForwardedComponent<T, TProps> = ComponentType<
  PropsWithoutRef<TProps> & RefAttributes<T>
>;

const forwardRefComponent = <T, TProps>(
  displayName: string,
  fn: ForwardRefRenderFunction<T, PropsWithoutRef<TProps>>,
): ForwardedComponent<T, TProps> => {
  fn.displayName = displayName;

  return forwardRef<T, TProps>(fn);
};

interface ContentProps {
  description?: ReactNode;
  rawDescription?: string;
  title?: ReactNode;
  iconBefore?: JSX.Element;
  iconAfter?: JSX.Element;
  align?: 'center' | 'start';
  disabled?: boolean;
}

export interface PopoverMenuButtonProps extends ContentProps {
  onClick: EventHandler<ReactMouseEvent<HTMLButtonElement>>;
  ariaOwns?: string;
  className?: string;
  disabled?: boolean;
  elementAfter?: ReactNode;
  testId?: TestId;
  // When set, wraps the button in a tooltip. A disabled button can't emit hover
  // events itself, so the tooltip's wrapper is what surfaces the explanation.
  tooltipContent?: ReactNode;
}

interface PopoverMenuLinkProps extends ContentProps {
  href: string;
  target?: string;
  onClick?: EventHandler<ReactMouseEvent<HTMLAnchorElement>>;
  testId?: TestId;
  className?: string;
  icon?: JSX.Element;
  external?: boolean;
  download?: string;
}

export const PopoverMenuItemSeparator: FunctionComponent<{
  fullWidth?: boolean;
  className?: string;
  ariaHidden?: boolean;
}> = ({ fullWidth, ariaHidden, className }) => (
  <li
    className={classNames(
      {
        [styles.separator]: true,
        [styles['separator--fullWidth']]: fullWidth,
      },
      className,
    )}
    aria-hidden={ariaHidden}
  ></li>
);

export const PopoverMenuItemContents: FunctionComponent<
  PropsWithChildren<ContentProps>
> = ({
  children,
  description,
  rawDescription,
  title,
  iconBefore,
  iconAfter,
  align = 'center',
  disabled,
}) => (
  <span
    className={classNames({
      [styles.item]: true,
      [styles.withoutMargins]: !!description,
      [styles.alignCenter]: align === 'center',
      [styles.alignStart]: align === 'start',
      [styles.disabled]: disabled,
    })}
  >
    {iconBefore && <span className={styles.iconBefore}>{iconBefore}</span>}
    <span className={styles.title}>
      <span className={styles.titleText}>{title || children}</span>
      {(description || rawDescription) && (
        <span className={styles.description}>
          {description ? description : rawDescription}
        </span>
      )}
    </span>
    {iconAfter && <span className={styles.iconAfter}>{iconAfter}</span>}
  </span>
);

export const PopoverMenuLink = forwardRefComponent<
  HTMLAnchorElement,
  PropsWithChildren<PopoverMenuLinkProps>
>(
  'PopoverMenuLink',
  (
    {
      testId,
      href,
      target,
      className,
      onClick,
      external = false,
      download,
      title,
      description,
      rawDescription,
      iconBefore,
      iconAfter,
      children,
      ...props
    }: PropsWithChildren<PopoverMenuLinkProps>,
    ref,
  ) => {
    const [hasFocusRing, mouseEvents] = useFocusRing(
      props as Partial<FocusRingEventListeners>,
    );
    const LinkComponent = external ? 'a' : RouterLink;

    const handleClick = useCallback(
      (e: ReactMouseEvent<HTMLAnchorElement>) => {
        // Stop propagation to prevent clicks from bubbling through React's
        // synthetic event system to parent components. This is necessary because
        // Popover uses React Portals, and React events in portals bubble through
        // the React component tree (not the DOM tree).
        e.stopPropagation();
        onClick?.(e);
      },
      [onClick],
    );

    const linkComponentProps = {
      href,
      onClick: handleClick,
      target,
      download,
      ...(LinkComponent === RouterLink
        ? { testId }
        : { 'data-testid': testId }),
    };

    return (
      <li>
        <LinkComponent
          className={classNames(
            styles.link,
            className,
            hasFocusRing && styles.focusRing,
          )}
          {...linkComponentProps}
          {...props}
          {...mouseEvents}
          ref={ref}
        >
          <PopoverMenuItemContents
            {...{
              title,
              description,
              rawDescription,
              iconBefore,
              iconAfter,
            }}
          >
            {children}
          </PopoverMenuItemContents>
        </LinkComponent>
      </li>
    );
  },
);

export const PopoverMenuButton = forwardRefComponent<
  HTMLButtonElement,
  PropsWithChildren<PopoverMenuButtonProps>
>(
  'PopoverMenuButton',
  (
    {
      onClick,
      children,
      className,
      disabled,
      testId,
      title,
      description,
      rawDescription,
      iconBefore,
      iconAfter,
      elementAfter,
      ariaOwns,
      tooltipContent,
      ...props
    }: PropsWithChildren<PopoverMenuButtonProps>,
    ref,
  ) => {
    const [hasFocusRing, mouseEvents] = useFocusRing(
      props as Partial<FocusRingEventListeners>,
    );

    const handleClick = useCallback(
      (e: ReactMouseEvent<HTMLButtonElement>) => {
        // Stop propagation to prevent clicks from bubbling through React's
        // synthetic event system to parent components. This is necessary because
        // Popover uses React Portals, and React events in portals bubble through
        // the React component tree (not the DOM tree).
        e.stopPropagation();
        onClick?.(e);
      },
      [onClick],
    );

    const button = (
      <button
        className={classNames(
          {
            [styles.button]: true,
            [styles.buttonWithElementAfter]: Boolean(elementAfter),
            [styles.focusRing]: hasFocusRing,
          },
          className,
        )}
        disabled={disabled}
        onClick={handleClick}
        data-testid={testId}
        {...props}
        {...mouseEvents}
        aria-owns={ariaOwns}
        ref={ref}
      >
        <PopoverMenuItemContents
          {...{
            title,
            description,
            rawDescription,
            iconBefore,
            iconAfter,
          }}
        >
          {children}
        </PopoverMenuItemContents>
      </button>
    );

    return (
      <li
        className={classNames({
          [styles.listItem]: true,
          [styles.listItemWithElementAfter]: Boolean(elementAfter),
        })}
      >
        {tooltipContent ? (
          <Tooltip content={tooltipContent}>{button}</Tooltip>
        ) : (
          button
        )}
        {elementAfter && (
          <span className={styles.elementAfter}>{elementAfter}</span>
        )}
      </li>
    );
  },
);

interface PopoverMenuProps {
  className?: string;
  testId?: TestId;
  ariaLabelledBy?: string;
  ariaLabel?: string;
  role?: 'listbox' | 'radiogroup';
  id?: string;
}

export const PopoverMenu: FunctionComponent<
  PropsWithChildren<PopoverMenuProps>
> = ({ className, children, testId, ariaLabelledBy, ariaLabel, role, id }) => (
  <div id={id} className={classNames(styles.popoverMenu, className)}>
    <ul
      data-testid={testId}
      aria-labelledby={ariaLabelledBy}
      aria-label={ariaLabel}
      role={role}
    >
      {children}
    </ul>
  </div>
);
